// reports/mustache.ts
//
// The mustache subset used by the on-device report templates.
//
// Report templates live on the backend and are rendered on the user's own
// device — by this file on web, and by its Dart twin in the mobile app. Both
// implementations must agree exactly: the same template and view-model have to
// produce byte-identical HTML on every platform, or the "one template, one
// design" guarantee quietly becomes "one template, two designs".
//
// That is why this is a hand-written 150-line subset rather than a mustache
// library. A library would pull in a much larger grammar whose corner cases we
// would then have to replicate in Dart, and we only need five constructs. The
// grammar below is the whole contract; the shared golden fixtures in
// __tests__/mustache.test.ts are duplicated verbatim on the Dart side.
//
//   {{path}}          HTML-escaped interpolation
//   {{{path}}}        raw interpolation (trusted markup: inline SVG, @font-face)
//   {{&path}}         raw interpolation, alias of the above
//   {{#path}}…{{/path}}   section
//   {{^path}}…{{/path}}   inverted section
//   {{.}}             the current context value (a scalar inside an array)
//
// Section semantics: an array renders its body once per item with the item
// pushed onto the context stack; a non-empty object or `true` renders the body
// once; everything falsey renders nothing. Inverted sections are the exact
// complement. Unresolved paths render as the empty string — a template that
// outgrows its view-model degrades to a gap, never to a crash mid-export.

/** A view-model value. Deliberately narrow: anything a template can render. */
export type TemplateValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | TemplateValue[]
  | { [key: string]: TemplateValue };

export type TemplateContext = Record<string, TemplateValue>;

/** Matches one tag: group 1 = sigil, group 2 = path. */
const TAG_RE = /\{\{(\{|&|#|\^|\/)?\s*([a-zA-Z0-9_.]+|\.)\s*\}?\}\}/g;

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escape for HTML text and double-quoted attribute values. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, ch => ESCAPES[ch]);
}

/**
 * Resolve a dotted path against the context stack, innermost frame first.
 *
 * Walking outward matters for the report templates: the sheet-level `{{meta.id}}`
 * has to stay reachable from inside a `{{#lengths.items}}` loop, which is how
 * the same header renders on every page of a multi-sheet report.
 */
function lookup(stack: TemplateValue[], path: string): TemplateValue {
  if (path === '.') return stack.at(-1);

  const parts = path.split('.');
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    let cur = stack[i];
    let ok = true;
    for (const part of parts) {
      if (cur === null || typeof cur !== 'object' || Array.isArray(cur) || !(part in cur)) {
        ok = false;
        break;
      }
      cur = (cur as Record<string, TemplateValue>)[part];
    }
    if (ok) return cur;
  }
  return undefined;
}

/** Whether a section with this value renders its body. */
function isTruthy(value: TemplateValue): boolean {
  if (value === null || value === undefined || value === false) return false;
  if (value === '' || value === 0) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/** Stringify a value for interpolation. `null`/`undefined` render as nothing. */
function stringify(value: TemplateValue): string {
  if (value === null || value === undefined || value === false) return '';
  if (value === true) return 'true';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  // Arrays and objects have no sensible inline form; rendering their default
  // stringification into a user's PDF would be worse than rendering nothing.
  return '';
}

/**
 * Find the `{{/name}}` that closes the section opened at `from`, accounting for
 * nested sections of the same name (the report templates nest `.items` loops
 * inside `.present` guards).
 */
function findSectionEnd(template: string, name: string, from: number): { bodyEnd: number; next: number } {
  const re = new RegExp(`\\{\\{([#^/])\\s*${name.replace(/\./g, '\\.')}\\s*\\}\\}`, 'g');
  re.lastIndex = from;
  let depth = 1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template)) !== null) {
    if (m[1] === '/') {
      depth -= 1;
      if (depth === 0) return { bodyEnd: m.index, next: m.index + m[0].length };
    } else {
      depth += 1;
    }
  }
  // Unclosed section: consume the rest rather than looping forever. The backend
  // balances tags at build time (TestSectionTagsAreBalanced), so reaching here
  // means a template was hand-edited past that gate.
  return { bodyEnd: template.length, next: template.length };
}

function renderWithStack(template: string, stack: TemplateValue[]): string {
  let out = '';
  let cursor = 0;
  TAG_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TAG_RE.exec(template)) !== null) {
    const [raw, sigil, path] = match;
    out += template.slice(cursor, match.index);

    if (sigil === '#' || sigil === '^') {
      const { bodyEnd, next } = findSectionEnd(template, path, TAG_RE.lastIndex);
      const body = template.slice(TAG_RE.lastIndex, bodyEnd);
      const value = lookup(stack, path);
      const truthy = isTruthy(value);

      if (sigil === '^') {
        if (!truthy) out += renderWithStack(body, stack);
      } else if (truthy) {
        if (Array.isArray(value)) {
          for (const item of value) out += renderWithStack(body, [...stack, item]);
        } else if (typeof value === 'object') {
          out += renderWithStack(body, [...stack, value]);
        } else {
          // A `true` / non-empty scalar guard renders its body once without
          // changing scope, so `{{#has_name}}{{name}}{{/has_name}}` works.
          out += renderWithStack(body, stack);
        }
      }

      cursor = next;
      TAG_RE.lastIndex = next;
      continue;
    }

    if (sigil === '/') {
      // A stray close tag (its opener was consumed by an outer frame) is dropped.
      cursor = match.index + raw.length;
      continue;
    }

    const text = stringify(lookup(stack, path));
    out += sigil === '{' || sigil === '&' ? text : escapeHtml(text);
    cursor = match.index + raw.length;
  }

  return out + template.slice(cursor);
}

/**
 * Render a report template against a view-model.
 *
 * Pure and synchronous by design: rendering has to be reproducible in a test and
 * in a golden fixture shared with the Dart implementation, so it takes no clock,
 * no locale and no DOM.
 */
export function renderTemplate(template: string, context: TemplateContext): string {
  return renderWithStack(template, [context]);
}
